import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import AnchorLink from 'react-anchor-link-smooth-scroll';

import Section from '../components/basic/layouts/Section';
import Layout from '../components/basic/layouts/NoMenu';
import { Grid, Cell } from '@layouts/Grid';
import Text from '../components/basic/Text';
import HubspotForm from '../components/PardotForm/HubspotForm';
import HTMLWrapper from '../components/basic/HTMLWrapper';
import documentToReactComponents from '../components/util/documentToReactComponents';
import Host from '../components/basic/GuestHost';
import WebinarButton from '../components/webinar/WebinarButton';

import { getDate, getTime } from '@basic/helper-functions/getFormattedDateTime';

import ClockIcon from '../images/webinar-images/Webinar_Icon_Clock.svg';
import CalendarIcon from '../images/webinar-images/Webinar_Icon_Calendar.svg';
import BasicPlayButton from '../components/basic/buttons/BasicPlayButton';

import theme from '../styles/theme';
import constants from '../constants';

const {
	colors,
	breakPoints: { screenQuery },
} = theme;

const { webinarTypes, webinarForm } = constants;

const IconTextContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: left;
	margin: 25px 0px 0px;
	> :nth-child(1) {
		margin-right: 40px;
	}
`;
const IconAndText = styled(AnchorLink)`
	display: flex;
	align-items: center;
	cursor: default;
	color: ${colors.grayscale.main};
	:hover {
		color: ${colors.grayscale.main};
	}
	h3 {
		margin-bottom: 0;
		margin-left: 10px;
		line-height: 0px;
	}
	margin-bottom: 20px;
`;

const FormWrapper = styled.div`
	margin-top: 70px;
	${screenQuery.medium} {
		margin-top: 0px;
	}
`;

const IndividualFormWrapper = styled.div`
	width: 100%;
	position: relative;
	background-color: ${colors.white};
	color: ${colors.black};
	border: none;
	padding: 60px;
	box-shadow: 0 2px 34px 0 rgba(0, 0, 0, 0.13);
`;

const HostSection = styled.div`
	display: ${props => (props.mobile ? `none` : `block`)};
	${screenQuery.large} {
		display: ${props => (props.mobile ? `block` : `none`)};
	}
	max-width: 625px;
	margin: 0 auto;
`;

const Hosts = ({ hosts }) =>
	hosts.map(host => (
		<Host
			image={host.personPhoto}
			title={host.personName}
			hideTitle
			description={host.description}
		/>
	));

const WebinarTime = ({
	onDemand,
	generalInfoLiveWebinarDateAndTime,
	timeZone,
}) => {
	const date = getDate(generalInfoLiveWebinarDateAndTime);
	const time = getTime(generalInfoLiveWebinarDateAndTime);

	return onDemand ? (
		<IconTextContainer>
			<IconAndText href="#webinar-form">
				<BasicPlayButton transparent color={colors.black} width={38} />
				<Text component="h3">On Demand</Text>
			</IconAndText>
		</IconTextContainer>
	) : generalInfoLiveWebinarDateAndTime ? (
		<IconTextContainer>
			<IconAndText>
				<img src={CalendarIcon} alt="Play Icon" />
				<Text component="h3">{date}</Text>
			</IconAndText>
			<IconAndText>
				<img src={ClockIcon} alt="Play Icon" />
				<Text component="h3">
					{time}
					{` `}
					{timeZone}
				</Text>
			</IconAndText>
		</IconTextContainer>
	) : null;
};

const RegistrationForm = ({
	onDemandExternalLink,
	generalInfoWebinarType,
	generalInfoForm,
	slug,
	form,
	...props
}) => {
	const onDemandWistia = generalInfoWebinarType === webinarTypes.onDemandWistia;

	return (
		<FormWrapper id="webinar-form">
			{generalInfoForm === webinarForm.hubspot && (
				<IndividualFormWrapper>
					<HubspotForm formId={props.hubspotFormId} />
				</IndividualFormWrapper>
			)}

			{generalInfoForm === webinarForm.zoom && (
				<WebinarButton
					buttonLink={props.zoomWebinarId}
					headerText="Sign up for this webinar"
					buttonText="Register on Zoom"
				/>
			)}

			{generalInfoForm === webinarForm.none && (
				<WebinarButton
					buttonLink={
						onDemandExternalLink
							? onDemandExternalLink
							: onDemandWistia
								? `/on-demand-webinar/${slug}/`
								: '/webinars'
					}
					headerText="View this webinar now"
					buttonText="Watch Now"
				/>
			)}
		</FormWrapper>
	);
};

const htmlWrapperStyles = {
	li: {
		marginBottom: 10,
	},
	p: {
		marginBottom: 20,
	},
};

const Page = ({ location, data }) => {
	const content = data.contentfulPageTemplateWebinar;
	const {
		generalInfoWebinarType,
		header,
		description,
		pageTitle,
		webinarHosts,
	} = content;
	const onDemand =
		generalInfoWebinarType === webinarTypes.onDemandWistia ||
		generalInfoWebinarType === webinarTypes.onDemandOther;

	const { form } = data;

	return (
		<Layout
			pageTitle={pageTitle}
			description={description}
			location={location}
			updatedAt={content.updatedAt}
			featuredImage={content.featuredImage?.file.url}
			noIndex={!content.isSearchable}
			lightHeader
			headerShadow
			backgroundColor={colors.grayscale.stone}
		>
			<Section>
				<Grid>
					{/* content section: */}
					<Cell className="webinar-content" span={7} spanLg={12}>
						<Text component="h1">{header}</Text>
						<WebinarTime onDemand={onDemand} {...content} />
						<HTMLWrapper oneColumnList>
							{documentToReactComponents(content.generalInfoWebinarDescription)}
						</HTMLWrapper>
						{webinarHosts && (
							<HostSection>
								<Text component="b2" css={{ padding: '50px 0px 33px' }}>
									Hosted By:
								</Text>
								<Hosts hosts={webinarHosts} />
							</HostSection>
						)}
					</Cell>
					{/* forms: */}
					<Cell className="webinar-form" span={5} spanLg={12}>
						<RegistrationForm {...content} form={form} />
						{webinarHosts && (
							<HostSection mobile>
								<Text component="b2" css={{ padding: '50px 0px 33px' }}>
									Hosted By:
								</Text>
								<Hosts hosts={webinarHosts} />
							</HostSection>
						)}
					</Cell>
				</Grid>
			</Section>
		</Layout>
	);
};

export default Page;

export const webinarQuery = graphql`
	query webinarQuery($contentful_id: String!) {
		contentfulPageTemplateWebinar(contentful_id: { eq: $contentful_id }) {
			contentful_id
			updatedAt
			createdAt
			title
			pageTitle
			description
			isSearchable
			slug
			header
			generalInfoLiveWebinarDateAndTime
			timeZone
			generalInfoWebinarType
			generalInfoForm
			onDemandExternalLink
			hubspotFormId
			zoomWebinarId
			featuredImage {
				file {
					url
				}
			}
			webinarHosts {
				... on ContentfulElementPersonWithBio {
					jobTitle
					personName
					personPhoto {
						gatsbyImageData(
							width: 125
							placeholder: BLURRED
							formats: [AUTO, WEBP, AVIF]
						)
					}
					description {
						raw
					}
					linkedin
				}
			}
			generalInfoWebinarDescription {
				raw
				references {
					... on ContentfulAsset {
						contentful_id
						__typename
						file {
							url
						}
					}
					... on ContentfulElementBasicVideo {
						contentful_id
						videoLink
						videoThumbnail {
							file {
								url
							}
						}
					}
				}
			}
		}
	}
`;
